import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { ToastrService } from '../notification/toastr.service';
import { UtilityService } from '../util/utility.service';
import { StorageService } from '../util/storage.service';
import { GtmService } from '../analytics/gtm.service';

import { Menu, MergedOutletTimings, OutletTimingResponse } from '@app/shared/models/api-response/outlet-timing-response';
import { OutletStatusStripService } from '../outlet-status-strip/outlet-status-strip.service';
import { MetaApiResponse } from '@app/shared/models/api-response/meta.api-response';
import { OutletState } from '@app/shared/models/api-response/catalog-api-response';

export interface TimeDetails {
    menus: Menu[];
    currentServerTime: number;
}

export interface PrimaryBrandData extends Menu {
    state: string;
}

interface MergedData extends MergedOutletTimings {
    preorderSlots: number[];
}

interface Model {
    timeDetails: TimeDetails;
    primaryBrandData: PrimaryBrandData;
    mergedData: MergedData;
}

@Injectable({
    providedIn: 'root'
})
export class PreorderService {
    multipleOf = 30;
    preorderInterval = 1800 * 1000; // 30 mins
    timeRanges = [];
    servicableBrandMetaData = new BehaviorSubject<Model>(null);

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private utilityService: UtilityService,
        private toastrService: ToastrService,
        private gtmService: GtmService,
        private outletStatusStripService: OutletStatusStripService
    ) {}

    async getPreorderSlots(brandIds: number[]) {
        if (!brandIds || !brandIds.length) {
            return;
        }

        const brand_id = [...new Set([...brandIds, environment.brand.id])];

        try {
            const outletDetails = await lastValueFrom(this.getOutletTimings(brand_id));
            const { current_server_time, menus, merged_outlet_timings, meta } = outletDetails;
            const { preorder_available } = merged_outlet_timings;

            const timeDetails = {
                menus,
                currentServerTime: current_server_time
            };

            const preorderTimeRange = this._preorderTime(merged_outlet_timings);
            const preorderSlots = this._preorderSlots(preorderTimeRange);
            const primaryBrandData = menus.find(menu => menu.brand_id === environment.brand.id);

            this.servicableBrandMetaData.next({
                timeDetails,
                primaryBrandData: {
                    state: this.utilityService.getBrandState(primaryBrandData),
                    ...primaryBrandData
                },
                mergedData: {
                    preorderSlots,
                    ...merged_outlet_timings,
                    preorder_available: Boolean(preorder_available && preorderSlots.length)
                }
            });

            this._handleOutletStatusStrip(meta, menus);
        } catch (err) {
            console.error(err);
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
        }
    }

    private _handleOutletStatusStrip(meta: MetaApiResponse['meta'], menus: Menu[]) {
        const primaryBrandMenu = menus.find(menu => menu.brand_id === environment.brand.id);
        const outletState = this.utilityService.getOutletState(primaryBrandMenu);
        const { heading, message, icon, allow_refresh } = meta;
        let outlet_status_strip = null;

        if (heading && message && outletState !== OutletState.PREORDER) {
            outlet_status_strip = {
                icon,
                heading,
                message,
                allow_refresh
            };
        }
        this.outletStatusStripService.hydrateOutletStatusStrip({ ...outlet_status_strip, outlet_state: outletState }, menus);
    }

    private getOutletTimings(brandIds) {
        const brands = brandIds.map(brand_id => {
            return brand_id;
        });
        const latitude = this.storageService.get('latitude') || 0;
        const longitude = this.storageService.get('longitude') || 0;
        const agent_id = this.storageService.get('agent_id');
        const outlet_id = this.storageService.get('outlet_id');
        const service_type = this.storageService.get('service_type');
        let queryParams = `?app_id=${environment.brand.id}&lat=${latitude}&lon=${longitude}&platform=web`;
        queryParams += agent_id != undefined ? `&agent_id=${agent_id}` : '';
        queryParams += outlet_id != undefined ? `&outlet_id=${outlet_id}` : '';
        queryParams += service_type != undefined ? `&service_type=${service_type}` : '';
        return this.http.post<OutletTimingResponse>(environment.catalogUrl + '/catalog/v2/outlets/outlet_timings' + queryParams, {
            brands
        });
    }

    private _preorderTime(outlet) {
        let timeRange: any;

        if (!outlet.outlet_timings) {
            return timeRange;
        }

        try {
            const currentTime = new Date();

            if (outlet.outlet_timings.sob_time && outlet.outlet_timings.cob_time) {
                const startOfBusinessTime = new Date(outlet.outlet_timings.sob_time.replace(/-/g, '/'));
                const closeOfBusinessTime = new Date(outlet.outlet_timings.cob_time.replace(/-/g, '/'));
                const interval = outlet.interval || 60;
                const startInterval = interval + 15;

                if (currentTime >= closeOfBusinessTime) {
                    return timeRange;
                }

                const startPreorderTime =
                    startOfBusinessTime <= currentTime && currentTime <= closeOfBusinessTime
                        ? new Date(this._nextMultipleOf(currentTime.valueOf() + startInterval * 60000))
                        : new Date(this._nextMultipleOf(startOfBusinessTime.valueOf() + 15 * 60000));

                timeRange = {
                    sop_time: startPreorderTime,
                    cop_time: closeOfBusinessTime
                };
            }
            return timeRange;
        } catch (error) {
            this.gtmService.logEvent('js_errors', {
                event_label: 'preorder_time_rangge',
                event_value: `Error in calculating preorder time range. id: ${outlet.id}. outlet_timings: ${JSON.stringify(
                    outlet.outlet_timings
                )}`
            });
        }
    }

    private _preorderSlots(timeRange) {
        if (!timeRange || !timeRange.sop_time || !timeRange.cop_time) {
            return [];
        }

        const sopTime = timeRange.sop_time.getTime();
        const copTime = timeRange.cop_time.getTime();
        const slots = [];
        const interval = this.preorderInterval;

        for (let i = sopTime; i <= copTime; i += interval) {
            slots.push(i);
        }

        return slots;
    }

    private _nextMultipleOf(time) {
        const time_in_minutes = time / 60000;
        if (time_in_minutes % 30 !== 0) {
            return new Date((time_in_minutes + (this.multipleOf - (time_in_minutes % this.multipleOf))) * 60000);
        }
        return time;
    }
}
